import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Search, Book, Layout, CreditCard } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { auth } from './firebase/config';
import { API_URL } from './config';
import LogoutButton from './components/LogoutButton';

const LibraryPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [hasLibrary, setHasLibrary] = useState(true);

    // NavItem component for side navigation
    const NavItem = ({ icon, label, path, active }) => (
        <button
            onClick={() => navigate(path)}
            className={`p-3 rounded-xl transition-colors ${active ? 'bg-purple-600/20 text-purple-400' : 'text-gray-400 hover:bg-purple-600/10'
                }`}
            title={label}
        >
            {icon}
        </button>
    );

    const QUERY_CONFIG = {
        staleTime: Infinity,
        cacheTime: 24 * 60 * 60 * 1000,
        retry: false,
    };

    const { data: papers = [], isLoading } = useQuery({
        queryKey: ['libraryPapers'],
        queryFn: async () => {
            const token = await auth.currentUser?.getIdToken();
            if (!token) return [];

            const response = await fetch(`${API_URL}/papers/library`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            const data = await response.json();

            if (response.status === 200 && data.message === "No library found") {
                setHasLibrary(false);
                return [];
            }

            if (!response.ok) {
                if (response.status === 401) {
                    navigate('/');
                    return [];
                }
                throw new Error('Failed to fetch library papers');
            }

            setHasLibrary(true);
            return Object.entries(data.papers || {}).map(([arxivId, paper]) => ({
                id: arxivId,
                ...paper
            }));
        },
        ...QUERY_CONFIG,
        enabled: !!auth.currentUser && hasLibrary,
    });

    const handlePaperClick = (paper) => {
        // Add more paper details to ensure we have all needed data
        const paperData = {
            ...paper,
            fromLibrary: true,
            timestamp: Date.now() // Add timestamp to ensure state change
        };

        // Use state and replace
        navigate('/workspace', {
            state: { paper: paperData },
            replace: true
        });
    };

    return (
        <div className="flex h-screen bg-black text-white">
            {/* Left Sidebar - Main Navigation */}
            <div className="w-16 border-r border-purple-900/30 flex flex-col items-center py-4">
                <div className="flex-1 flex flex-col items-center space-y-6">
                    <NavItem icon={<Search />} label="Search" path="/search" active={location.pathname === '/search'} />
                    <NavItem icon={<Layout />} label="Workspace" path="/workspace" active={location.pathname === '/workspace'} />
                    <NavItem icon={<Book />} label="Library" path="/library" active={location.pathname === '/library'} />
                    <NavItem icon={<CreditCard />} label="Subscription" path="/subscription" active={location.pathname === '/subscription'} />
                </div>
                <LogoutButton />
            </div>

            {/* Main Content Area */}
            <div className="flex-1 flex flex-col overflow-hidden">
                {/* Header */}
                <header className="h-16 border-b border-purple-900/30 flex items-center px-6">
                    <h1 className="text-2xl font-semibold text-purple-400">My Library</h1>
                </header>

                {/* Scrollable Content */}
                <main className="flex-1 overflow-auto p-6">
                    {isLoading ? (
                        <div className="flex items-center justify-center h-64">
                            <div className="flex flex-col items-center gap-4">
                                <div className="w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full animate-spin" />
                                <p className="text-gray-400">Loading your papers...</p>
                            </div>
                        </div>
                    ) : papers.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-64 text-gray-400">
                            <Search className="w-12 h-12 mb-4 opacity-20" />
                            <p className="text-lg mb-2">Your library is empty</p>
                            <p className="text-sm">Search for papers to add them to your library</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {papers.map((paper) => (
                                <div
                                    key={paper.id}
                                    onClick={() => handlePaperClick(paper)}
                                    className="p-6 rounded-lg border border-purple-900/30 bg-purple-900/10 
                                             hover:bg-purple-900/20 transition-colors cursor-pointer
                                             flex flex-col h-full"
                                >
                                    <h2 className="text-lg font-medium text-purple-400 mb-2 line-clamp-2 flex-none">
                                        {paper.title}
                                    </h2>
                                    <div className="flex flex-wrap gap-2 mb-4 flex-none">
                                        {paper.authors.slice(0, 3).map((author, index) => (
                                            <span
                                                key={index}
                                                className="px-2 py-1 text-xs bg-purple-900/30 rounded-full text-purple-300"
                                            >
                                                {author}
                                            </span>
                                        ))}
                                        {paper.authors.length > 3 && (
                                            <span className="px-2 py-1 text-xs bg-purple-900/30 rounded-full text-purple-300">
                                                +{paper.authors.length - 3} more
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-sm text-gray-400 line-clamp-4 flex-grow">
                                        {paper.summary}
                                    </p>
                                    <div className="mt-4 text-xs text-gray-500 flex-none">
                                        Published: {new Date(paper.published).toLocaleDateString()}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </main>
            </div>
        </div>
    );
};

export default LibraryPage;